import * as React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  ClickAwayListener,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useDispatch, useSelector } from "react-redux";
import {
  getSellerNotificationTypes,
  getSellerNotificationAllUsers,
  putSellerNotificationUserData,
  getPostSellerNotificationUser,
  getPostSellerUserForNotif,
  resetInputFormUser,
  reset,
} from "../../../redux/slices/sellerNotificationSlice";
import { validateEmail } from "../../../utils";
import { ROLES } from "../../../utils/constants";

const ToolBox = ({
  email,
  id,
  notifs,
  allNotifs,
  seller_id,
  filter,
  isAdmin,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery((theme) =>
    theme.breakpoints.between("sm", "md")
  );

  const [newNotifs, setNewNotifs] = React.useState([]);

  React.useEffect(() => {
    notifs && setNewNotifs(notifs.split(","));
  }, [notifs]);

  const handleCheckboxChange = (e) => {
    setNewNotifs((prevNotifs) => {
      if (prevNotifs.includes(e.target.value)) {
        return prevNotifs.filter((item) => !(item === e.target.value));
      }

      return [...prevNotifs, e.target.value];
    });
  };

  const handleNotifUpdate = () => {
    dispatch(
      putSellerNotificationUserData({
        user_id: id,
        data: newNotifs,
        seller_id,
        filter,
      })
    );
    dispatch(reset());
  };

  let isButtonDisabled = false;
  if (isAdmin) {
    isButtonDisabled = true;
  } else {
    isButtonDisabled = !(
      !newNotifs.every((item) => notifs.indexOf(item) !== -1) ||
      newNotifs.length !== notifs.split(",").length
    );
  }

  return (
    <Grid
      item
      xs={12}
      sx={{
        ...(!isMobile && {
          position: "absolute",
          left: "51%",
          maxWidth: "43% !important",
        }),
      }}
    >
      <Collapse in={true}>
        <Box
          sx={{
            bgcolor: "#f5f5f9",
            p: "1.5rem",
            mb: isMobile ? 0 : "2rem",
            borderRadius: isMobile ? 0 : ".5rem",
            position: "relative",
            "&::after": {
              position: "absolute",
              content: "''",
              top: isMobile ? "-.6rem" : "3rem",
              right: isMobile ? "calc(10% - 10px)" : "100%",
              width: 0,
              height: 0,
              borderStyle: "solid",
              borderWidth: isMobile ? "0 10px 10px 10px" : "10px 10px 10px 0",
              borderColor: isMobile
                ? "transparent transparent #f5f5f9 transparent"
                : "transparent  #f5f5f9 transparent transparent",
            },
          }}
        >
          <Grid item xs={12}>
            {" "}
            <Typography fontSize={".875rem"} color={theme.greyText} mb={1}>
              Notifications being sent to <strong>{email}</strong>
            </Typography>
            {isAdmin && (
              <Typography fontSize={".875rem"} color={theme.greyText} my={2}>
                Please note that Admins cannot opt out from receiving
                notifications.
              </Typography>
            )}
            {allNotifs.map((item) => {
              if (item.notification_type_id === "ALL") return null;
              return (
                <FormControlLabel
                  sx={{
                    display: "flex",
                    mb: 1,
                    "& .MuiCheckbox-root.Mui-disabled": {
                      color: theme.palette.disabledButton,
                    },
                  }}
                  value={item.notification_type_id}
                  disabled={isAdmin}
                  control={
                    <Checkbox
                      size="small"
                      checked={newNotifs.includes(item.notification_type_id)}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label={
                    <Typography
                      fontSize={".875rem"}
                      fontWeight={600}
                      color={isAdmin && theme.palette.greyText}
                    >
                      {item.notification_type_en}
                    </Typography>
                  }
                />
              );
            })}
          </Grid>
          <Grid item container justifyContent={isMobile ? "unset" : "end"}>
            <Button
              disabled={isButtonDisabled}
              variant="contained"
              sx={{
                borderRadius: 3,
                py: 1.5,
                px: 4,

                mt: 4,
                ...(isMobile && { width: "100%" }),
                fontSize: "1rem",
                textTransform: "none",
                lineHeight: "1.5rem",
                fontWeight: 600,
              }}
              onClick={handleNotifUpdate}
            >
              Save Updates
            </Button>
          </Grid>
        </Box>
      </Collapse>
    </Grid>
  );
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  autoFocus: false,
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const INITIAL_VALUES = {
  user_email: "",
  user_first_name: "",
  user_last_name: "",
  user_opted_notifications: [],
};

const Notifications = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery((theme) =>
    theme.breakpoints.between("sm", "md")
  );
  const isTablet = useMediaQuery((theme) =>
    theme.breakpoints.between("md", "lg")
  );

  const [showProfile, setShowProfile] = React.useState(false);
  const [currentNotif, setCurrentNotif] = React.useState("ALL");
  const [formData, setFormData] = React.useState({
    user_email: "",
    user_first_name: "",
    user_last_name: "",
    user_opted_notifications: [],
  });

  const [formValidation, setFormValidation] = React.useState(INITIAL_VALUES);

  const [formError, setFormError] = React.useState(INITIAL_VALUES);
  const [searchInput, setSearchInput] = React.useState("");
  const [searchInputNotif, setSearchInputNotif] = React.useState(false);

  const state = useSelector(
    ({
      sellerNotification: {
        notificationTypes,
        allNotifUsers,
        toggle,
        allUsers,
      },
      seller: {
        selectedSeller: { seller_id },
        msal,
      },
    }) => ({
      notificationTypes,
      allNotifUsers,
      seller_id,
      allUsers,
      toggle,
      msal,
    })
  );

  console.log(state);

  const [open, setOpen] = React.useState(null);
  const [disableInputForm, setDisableInputForm] = React.useState(false);
  const [localAllNotifs, setLocalAllNotifs] = React.useState([]);

  React.useEffect(() => {
    if (!state.notificationTypes?.length)
      dispatch(getSellerNotificationTypes());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (state?.toggle) {
      setOpen(false);
    }
    setLocalAllNotifs(state.allNotifUsers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.allNotifUsers]);

  React.useEffect(() => {
    dispatch(
      getSellerNotificationAllUsers({
        seller_id: state.seller_id,
        filter: "ALL",
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = (item) => {
    if (item === open) setOpen(false);
    else setOpen(item);
  };

  const handleCheckboxChange = (e) => {
    setFormData((prevData) => {
      if (prevData.user_opted_notifications.includes(e.target.value)) {
        return {
          ...prevData,
          user_opted_notifications: prevData.user_opted_notifications.filter(
            (item) => !(item === e.target.value)
          ),
        };
      }

      return {
        ...prevData,
        user_opted_notifications: [
          ...prevData.user_opted_notifications,
          e.target.value,
        ],
      };
    });
  };

  const handleInputChange = (value) => {
    dispatch(
      getPostSellerUserForNotif({
        seller_id: state.seller_id,
        searchTerm: value,
      })
    );
  };

  const handleFormData = (e) => {
    setFormData((prevData) => ({
      ...prevData,
      [e.target.name]: e.target.value,
    }));

    if (e.target.name === "user_email") {
      let isValid = validateEmail(e.target.value.trim());
      let msg = "Please enter a valid email (personal emails not allowed)";
      if (
        !isValid &&
        state.allNotifUsers.filter(
          (item) => item.user_email === e.target.value.trim()
        ).length
      ) {
        msg = "There is already a recipient associated with this email";
        isValid = true;
      }
      setFormValidation((prevState) => ({
        ...prevState,
        user_email: Boolean(!isValid),
      }));
      setFormError((prevState) => ({
        ...prevState,
        user_email: !isValid ? "" : msg,
      }));
    } else if (e.target.name === "user_first_name") {
      const isValid = e.target.value.trim().length;
      setFormValidation((prevState) => ({
        ...prevState,
        user_first_name: Boolean(isValid),
      }));
      setFormError((prevState) => ({
        ...prevState,
        user_first_name: isValid ? "" : "First Name should not be empty",
      }));
    } else if (e.target.name === "user_last_name") {
      const isValid = e.target.value.trim().length;
      setFormValidation((prevState) => ({
        ...prevState,
        user_last_name: Boolean(isValid),
      }));
      setFormError((prevState) => ({
        ...prevState,
        user_last_name: isValid ? "" : "Last Name should not be empty",
      }));
    }
  };

  const handleNewRecipientSubmit = () => {
    dispatch(
      getPostSellerNotificationUser({
        seller_id: state.seller_id,
        data: formData,
        filter: currentNotif,
      })
    );
    setFormData({
      user_first_name: "",
      user_last_name: "",
      user_email: "",
      user_opted_notifications: [],
    });
    setDisableInputForm(false);
    dispatch(resetInputFormUser());
  };

  const handleCurrentNotifChange = (e) => {
    setCurrentNotif(e.target.value);
    dispatch(
      getSellerNotificationAllUsers({
        seller_id: state.seller_id,
        filter: e.target.value,
      })
    );
    setOpen(false);
  };

  const handleSearchInput = () => {
    setSearchInputNotif(() => true);
    setLocalAllNotifs((prev) => {
      return prev.filter(
        (item) =>
          item.user_first_name.includes(searchInput) ||
          item.user_last_name.includes(searchInput)
      );
    });
    setSearchInputNotif(() => false);
  };

  const handleKeyDown = (e) => {
    if (e.which === 13 && e.shiftKey === false && searchInput) {
      setSearchInputNotif(() => true);
      setLocalAllNotifs((prev) => {
        return prev.filter(
          (item) =>
            item.user_first_name.includes(searchInput) ||
            item.user_last_name.includes(searchInput)
        );
      });
      setSearchInputNotif(() => false);
    }
  };

  const toggleProfile = () => setShowProfile(!showProfile);

  return (
    <Box>
      <Divider sx={{ ...(isMobile && { mx: -3 }) }} />
      {state.msal.roles.some(
        (role) => role === ROLES.SUPER_ADMIN || role === ROLES.BUSINESS_ADMIN
      ) ? (
        <Accordion
          disableGutters
          elevation={0}
          sx={{ "&:before": { height: 0 } }}
          expanded={showProfile}
          onChange={toggleProfile}
        >
          <AccordionSummary
            sx={{
              "& .MuiAccordionSummary-content": {
                flexGrow: 0,
              },
              justifyContent: "start",
              color: theme.palette.darkBlue,
            }}
            expandIcon={
              <KeyboardArrowDownIcon
                sx={{ color: `${theme.palette.darkBlue} !important` }}
              />
            }
          >
            <Typography fontWeight={600}>Add Recipient</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container flexDirection={"column"}>
              <Grid item container columnSpacing={6} rowSpacing={3}>
                <Grid item xs={12} md={5} lg={5}>
                  <Autocomplete
                    value={formData.user_email}
                    options={state.allUsers.map((item) => item.user_email)}
                    freeSolo
                    onInput={(e, value) => {
                      e.target.value && handleInputChange(e.target.value);
                      handleFormData({
                        target: {
                          name: "user_email",
                          value: e.target.value ?? "",
                        },
                      });
                    }}
                    onChange={(e, value) => {
                      console.log("hahahah", value);
                      if (value === null) {
                        dispatch(resetInputFormUser());
                        if (disableInputForm) {
                          setFormData(INITIAL_VALUES);
                          setDisableInputForm(false);
                        }
                      }
                      handleFormData({
                        target: { name: "user_email", value: value ?? "" },
                      });
                      const user = state.allUsers.filter(
                        (item) => item.user_email === value
                      );
                      if (user.length) {
                        handleFormData({
                          target: {
                            name: "user_first_name",
                            value: user[0].user_first_name,
                          },
                        });
                        handleFormData({
                          target: {
                            name: "user_last_name",
                            value: user[0].user_last_name,
                          },
                        });
                        setDisableInputForm(true);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        autoComplete={"off"}
                        name="user_email"
                        error={Boolean(formError.user_email)}
                        helperText={formError.user_email}
                        sx={{ mt: 2.5, width: "100%" }}
                        value={formData.user_email}
                        // onChange={handleFormData}
                        required
                        size="small"
                        label="Email"
                        InputLabelProps={{
                          style: { fontSize: ".8rem", fontWeight: 600 },
                        }}
                        inputProps={{
                          ...params.inputProps,
                          style: {
                            fontWeight: 600,
                            padding: ".4rem 0",
                          },
                        }}
                      />
                    )}
                  />

                  <TextField
                    variant="standard"
                    autoComplete={"off"}
                    name="user_first_name"
                    error={Boolean(formError.user_first_name)}
                    helperText={formError.user_first_name}
                    sx={{ mt: 2.5, width: "100%", fontWeight: 600 }}
                    value={formData.user_first_name}
                    onChange={handleFormData}
                    disabled={disableInputForm}
                    required
                    size="small"
                    label="First Name"
                    InputLabelProps={{
                      style: { fontSize: ".8rem", fontWeight: 600 },
                    }}
                    inputProps={{
                      style: {
                        fontWeight: 600,
                        padding: ".4rem 0",
                      },
                    }}
                  />

                  <TextField
                    variant="standard"
                    disabled={disableInputForm}
                    autoComplete={"off"}
                    name="user_last_name"
                    error={Boolean(formError.user_last_name)}
                    helperText={formError.user_last_name}
                    sx={{ mt: 2.5, width: "100%" }}
                    value={formData.user_last_name}
                    onChange={handleFormData}
                    required
                    size="small"
                    label="Last Name"
                    InputLabelProps={{
                      style: { fontSize: ".8rem", fontWeight: 600 },
                    }}
                    inputProps={{
                      style: {
                        fontWeight: 600,
                        padding: ".4rem 0",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={7} lg={7}>
                  <Typography
                    fontSize={".875rem"}
                    color={theme.greyText}
                    mb={1}
                  >
                    Add to these notifications lists
                  </Typography>
                  {state.notificationTypes.map((item) => {
                    if (item.notification_type_id === "ALL") return null;
                    return (
                      <FormControlLabel
                        sx={{ display: "flex" }}
                        value={item.notification_type_id}
                        control={
                          <Checkbox
                            size="small"
                            checked={formData.user_opted_notifications.includes(
                              item.notification_type_id
                            )}
                            onChange={handleCheckboxChange}
                          />
                        }
                        label={
                          <Typography fontSize={".875rem"} fontWeight={600}>
                            {item.notification_type_en}
                          </Typography>
                        }
                      />
                    );
                  })}
                </Grid>
              </Grid>
              <Grid item container justifyContent={isMobile ? "center" : "end"}>
                <Button
                  disabled={
                    !(
                      Object.values(formValidation).every((item) => item) &&
                      formData.user_opted_notifications.length
                    )
                  }
                  variant="contained"
                  sx={{
                    borderRadius: 3,
                    width: isMobile ? "100%" : "unset",
                    py: 1.5,
                    px: 4,
                    mt: 4,
                    fontSize: "1rem",
                    textTransform: "none",
                    lineHeight: "1.5rem",
                    fontWeight: 600,
                  }}
                  onClick={handleNewRecipientSubmit}
                >
                  Add Recipient
                </Button>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      ) : null}
      <Divider sx={{ ...(isMobile && { mx: -3 }) }} />
      <Grid container columnSpacing={4} mt={2} rowGap={2}>
        <Grid item xs={12} md={4} lg={4}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              rowGap: 3,
              columnGap: 3,
            }}
          >
            <TextField
              variant="standard"
              name="searchInput"
              value={searchInput}
              autoComplete={"off"}
              sx={{ mt: 2.5, width: "100%" }}
              onKeyDown={handleKeyDown}
              onChange={(e) => {
                setSearchInput(e.target.value);
                if (!e.target.value.length) {
                  setLocalAllNotifs(state.allNotifUsers);
                }
              }}
              size="small"
              label="Search for Recipient"
              InputLabelProps={{
                style: { fontWeight: 600, color: theme.navbarBg },
              }}
              inputProps={{
                style: {
                  fontWeight: 600,
                  padding: ".4rem 0",
                },
              }}
              InputProps={{
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    {
                      <IconButton
                        sx={{ visibility: !true && "hidden" }}
                        onClick={() => {
                          setSearchInput("");
                          setSearchInputNotif(false);
                          setLocalAllNotifs(state.allNotifUsers);
                        }}
                      >
                        <ClearIcon />{" "}
                      </IconButton>
                    }
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
          lg={4}
          container={searchInput.length ? true : false}
        >
          {searchInput ? (
            <Button
              size="large"
              variant="contained"
              sx={{
                ...(isMobile || isTablet
                  ? { display: "flex", flex: 1 }
                  : { width: "6.5rem", maxHeight: "3rem" }),
                // width: isMobile ? "100%" : "6.5rem",
                boxShadow: "0 0 1rem #ccc",
                borderRadius: ".75rem",
                alignSelf: "end",
              }}
              disabled={!searchInput.length}
              onClick={handleSearchInput}
            >
              {searchInputNotif ? (
                <CircularProgress
                  thickness={6}
                  size={"2rem"}
                  sx={{ color: theme.palette.primary.contrastText }}
                />
              ) : (
                <SearchIcon fontSize="large" />
              )}
            </Button>
          ) : (
            <FormControl
              sx={{ display: "flex", mt: ".6rem" }}
              variant="standard"
            >
              <InputLabel id="status-label">Show recipients of</InputLabel>
              <Select
                labelId="status-label"
                id="demo-multiple-checkbox"
                value={currentNotif}
                onChange={handleCurrentNotifChange}
                renderValue={(selected) => {
                  return state.notificationTypes.find(
                    (item) => item.notification_type_id === selected
                  )?.notification_type_en;
                }}
                MenuProps={MenuProps}
                sx={{
                  fontWeight: 600,
                  padding: ".4rem 0",
                }}
              >
                {state.notificationTypes.map((item) => {
                  return (
                    <MenuItem value={item.notification_type_id}>
                      {/* <Checkbox
                        checked={}
                      /> */}
                      <ListItemText
                        sx={{ p: 1 }}
                        primary={`${item.notification_type_en}`}
                      />
                      {currentNotif === item.notification_type_id && (
                        <CheckIcon color="success" />
                      )}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}
        </Grid>
      </Grid>

      {!localAllNotifs.length && searchInput.length ? (
        <Box sx={{ my: 2 }}>
          <Typography textAlign={"center"} mb={2} fontSize={"1rem"}>
            Sorry, we couldn't find any matches for this recipient.
          </Typography>
          <Typography textAlign={"center"} fontSize={"1rem"}>
            This person may have unsubscribed from receiving notifications
            emails.
          </Typography>
        </Box>
      ) : null}

      {localAllNotifs.map((item) => (
        <Grid container mt={4}>
          <Grid
            item
            container
            xs={12}
            md={6}
            lg={6}
            sx={{
              border: `1px solid ${theme.palette.lightBlue}`,
              borderRadius: ".5rem",
            }}
          >
            <Grid item xs={8} md={9} lg={9} p={isMobile ? 2.5 : 3.5}>
              <Typography
                fontSize={isMobile ? "1rem" : "1.2rem"}
                fontWeight={600}
              >
                {item.user_first_name} {item.user_last_name}
              </Typography>
              <Typography
                color={theme.palette.primary.main}
                fontSize={isMobile ? ".8rem" : ".875rem"}
                fontWeight={600}
              >
                {item.role}
              </Typography>
            </Grid>
            <Grid
              item
              xs={2}
              md={1}
              lg={1}
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <NotificationsIcon
                htmlColor={theme.palette.greyText}
                fontSize={"medium"}
              />
              <Typography fontSize={".875rem"}>
                {" "}
                {item.count.split(",").length}/3
              </Typography>
            </Grid>

            <Grid
              item
              xs={2}
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              {state.msal.roles.some(
                (role) =>
                  role === ROLES.SUPER_ADMIN || role === ROLES.BUSINESS_ADMIN
              ) ? (
                <ModeEditIcon
                  fontSize="large"
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleClick(item.user_id)}
                  htmlColor={theme.palette.primary.main}
                />
              ) : null}
            </Grid>
            {isMobile && item.user_id === open && (
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <div>
                  <ToolBox
                    email={item.user_email}
                    id={item.user_id}
                    notifs={item.count}
                    allNotifs={state.notificationTypes}
                    seller_id={state.seller_id}
                    filter={currentNotif}
                    isAdmin={item.role.toLowerCase() === "admin"}
                  />
                </div>
              </ClickAwayListener>
            )}
          </Grid>

          {!isMobile && item.user_id === open && (
            <ClickAwayListener onClickAway={() => setOpen(false)}>
              <div>
                <ToolBox
                  email={item.user_email}
                  id={item.user_id}
                  allNotifs={state.notificationTypes}
                  notifs={item.count}
                  seller_id={state.seller_id}
                  filter={currentNotif}
                  isAdmin={item.role.toLowerCase() === "admin"}
                />
              </div>
            </ClickAwayListener>
          )}
        </Grid>
      ))}
    </Box>
  );
};

export default Notifications;
